import styled from "styled-components";

const StepperNav = styled.nav`
  margin-bottom: 1.5rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 0.15rem;
`;

const StepperTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: min(100%, 520px);
`;

const Connector = styled.div`
  flex: 1;
  min-width: 12px;
  height: 2px;
  background: ${(p) => (p.$done ? "#19bc31" : "#e0e0e0")};
  border-radius: 1px;
`;

const StepItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: ${(p) => (p.$active || p.$done ? 600 : 500)};
  color: ${(p) => (p.$active ? "#383838" : p.$done ? "#383838" : "#a1a0a0")};

  @media (min-width: 576px) {
    font-size: 0.8rem;
  }
`;

const StepDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.65rem;
  height: 1.65rem;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  background: ${(p) => {
    if (p.$done) return "#19bc31";
    if (p.$active) return "var(--color-primary)";
    return "#e5e5e5";
  }};
  color: ${(p) => (p.$done || p.$active ? "#fff" : "#525252")};
  box-shadow: ${(p) => (p.$active ? "0 0 0 4px var(--color-primary-soft)" : "none")};
`;

const StepLabel = styled.span`
  max-width: 7.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    max-width: none;
  }
`;

function resolveActiveIndex(steps, progress, activeStepId) {
  if (activeStepId) {
    const idx = steps.findIndex((s) => s.id === activeStepId);
    if (idx >= 0) return idx;
  }
  const firstIncomplete = steps.findIndex((s) => !progress?.[s.id]);
  return firstIncomplete >= 0 ? firstIncomplete : steps.length - 1;
}

export function getStepDisplayLabel(step, { isDone, isActive } = {}) {
  if (!step) return "";
  if (step.id === "account" && isActive && !isDone) {
    return "Create account";
  }
  if (step.skippable && isActive && !isDone) {
    return `${step.label} (optional)`;
  }
  return step.label;
}

/**
 * Config-driven onboarding stepper (same shape as DEFAULT_INVESTOR_ONBOARDING_FLOW).
 * TODO(Admin): steps array will come from org settings in UniqueCoop manager portal.
 */
export default function InvestorOnboardingStepper({ flow, progress = {}, activeStepId }) {
  const steps = flow?.steps || [];
  if (!steps.length) return null;

  const activeIndex = resolveActiveIndex(steps, progress, activeStepId);

  return (
    <StepperNav aria-label="Onboarding progress">
      <StepperTrack>
        {steps.map((step, index) => {
          const isDone = Boolean(progress?.[step.id]);
          const isActive = index === activeIndex && !isDone;
          const showConnector = index > 0;
          const displayLabel = getStepDisplayLabel(step, { isDone, isActive });

          return (
            <div key={step.id} className="d-flex align-items-center flex-grow-1 min-w-0">
              {showConnector ? <Connector $done={index <= activeIndex || isDone} aria-hidden /> : null}
              <StepItem $active={isActive} $done={isDone} aria-current={isActive ? "step" : undefined}>
                <StepDot $done={isDone} $active={isActive} aria-hidden>
                  {isDone ? "✓" : index + 1}
                </StepDot>
                <StepLabel title={displayLabel}>{displayLabel}</StepLabel>
              </StepItem>
            </div>
          );
        })}
      </StepperTrack>
    </StepperNav>
  );
}

export function getOnboardingStepSubtitle(flow, activeStepId, progress = {}) {
  const steps = flow?.steps || [];
  const index = resolveActiveIndex(steps, progress, activeStepId);
  const step = steps[index];
  const total = steps.length;
  if (!step) return null;
  return `Step ${index + 1} of ${total} — ${step.label}`;
}
